// src/components/UnauthGuard.js
import { Navigate, useLocation } from "react-router-dom";
import useAuth from "../hooks/useAuth";

const UnauthGuard = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/"; // Redirect to previous page or home

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>

        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse animation-delay-1000"></div>
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-full blur-3xl animate-spin"
            style={{ animationDuration: "20s" }}
          ></div>
        </div>

        <div className="relative">
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-2xl">
            <div className="text-center">
              {/* Main loading icon with rotating rings */}
              <div className="relative w-24 h-24 mx-auto mb-6">
                {/* Outer ring */}
                <div className="absolute inset-0 rounded-full border-4 border-slate-700/30"></div>

                {/* Animated rings */}
                <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-500 animate-spin"></div>
                <div
                  className="absolute inset-2 rounded-full border-4 border-transparent border-t-purple-500 animate-spin"
                  style={{
                    animationDirection: "reverse",
                    animationDuration: "1.5s",
                  }}
                ></div>
                <div
                  className="absolute inset-4 rounded-full border-4 border-transparent border-t-blue-400 animate-spin"
                  style={{ animationDuration: "2s" }}
                ></div>

                {/* Center icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Loading text with animation */}
              <h3 className="text-2xl font-bold text-white mb-3">
                Verifying Authentication
              </h3>

              {/* Animated dots */}
              <div className="flex items-center justify-center space-x-1 mb-4">
                <span className="text-gray-400">Please wait</span>
                <div className="flex space-x-1">
                  <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce"></div>
                  <div
                    className="w-1 h-1 bg-purple-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="w-1 h-1 bg-blue-400 rounded-full animate-bounce"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-slate-700/50 rounded-full h-2 mb-6">
                <div
                  className="h-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full animate-pulse"
                  style={{ width: "60%" }}
                ></div>
              </div>

              {/* Additional status text */}
              <p className="text-gray-400 text-sm">Securing your session...</p>
            </div>
          </div>

          {/* Floating particles */}
          <div className="absolute -top-20 -left-20 w-4 h-4 bg-blue-400/30 rounded-full animate-ping"></div>
          <div
            className="absolute -top-10 -right-16 w-2 h-2 bg-purple-400/40 rounded-full animate-ping"
            style={{ animationDelay: "0.5s" }}
          ></div>
          <div
            className="absolute -bottom-16 -left-12 w-3 h-3 bg-blue-300/20 rounded-full animate-ping"
            style={{ animationDelay: "1s" }}
          ></div>
          <div
            className="absolute -bottom-8 -right-20 w-2 h-2 bg-purple-300/30 rounded-full animate-ping"
            style={{ animationDelay: "1.5s" }}
          ></div>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  return children;
};

export default UnauthGuard;
